import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { investorActions } from '@/store/investor';
import { RootRouter, ScrollToTop } from '@/routing';
import { goToRegistration } from '@/utils/helpers/investor';
import { Loading, Overlay, Toast } from '@/components';
import { userActions, userReducer } from '@/store/user';
import { ExternalLink } from '@/ui/atoms/Link';
import { useTranslation } from 'react-i18next';
import { useGetStatics, useCountries, usePopups } from '@/hooks';
import styled from 'styled-components';
import { Spin, Typography } from 'antd';
import { BREAKPOINTS } from '@/ui';

const USER_GUIDE_SEEN_KEY = 'hasSeenUserGuide';

const App = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAvailableLanguages, setCountries, languages } = useCountries();
  const { showInfoToast } = usePopups();
  const { loader } = useSelector((state) => state.entities);
  const { userRole, showUserGuide, showBrowserCompatibility } = useSelector((state) => state.user);
  const [routerMode, setRouterMode] = useState('');

  const { data: statics } = useGetStatics();

  const emailStatic = useMemo(
    () => statics && statics.find((item) => item.title === 'email'),
    [statics]
  );

  const phoneStatic = useMemo(
    () => statics && statics.find((item) => item.title === 'phone'),
    [statics]
  );

  useEffect(() => {
    getAvailableLanguages();

    const userRole = localStorage.getItem('userRole');
    const token = localStorage.getItem('token');

    if (userRole && token) {
      dispatch(userActions.setToken({ userRole: userRole, accessToken: token }));
    } else {
      dispatch(userActions.setRole(null));
    }

    const hasSeenGuide = localStorage.getItem(USER_GUIDE_SEEN_KEY);
    if (hasSeenGuide) {
      dispatch(userReducer.setShowBrowserCompatibility(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (languages) {
      setCountries().then(() => {
        console.log('countries and languages are set');
      });
    }
  }, [languages, setCountries]);

  useEffect(() => {
    (async () => {
      switch (userRole) {
        case 'investor': {
          try {
            const investor = await dispatch(investorActions.getInvestor());
            if (
              !investor.conditionsRegistration.verifiedEmail ||
              !investor.conditionsRegistration.verifiedPhone
            ) {
              goToRegistration(investor, navigate);
              setRouterMode('reg');
              dispatch(userActions.setRole(null));
            } else {
              setRouterMode(userRole);
            }
          } catch (e) {
            dispatch(userActions.setRole(null));
            showInfoToast(e, true);
            console.log(e);
          }

          break;
        }
        case 'admin': {
          setRouterMode(userRole);
          break;
        }
        case 'superAdmin': {
          setRouterMode(userRole);
          break;
        }
        case null: {
          setRouterMode('reg');
          break;
        }
        case undefined: {
          break;
        }
        default: {
          setRouterMode('reg');
        }
      }
    })();
  }, [userRole]);

  const handleCloseUserGuide = () => {
    localStorage.setItem(USER_GUIDE_SEEN_KEY, 'true');
    dispatch(userReducer.setShowBrowserCompatibility(false));
  };

  return (
    <ScrollToTop>
      {routerMode && (
        <>
          <AppContainer>
            <RootRouter routerMode={routerMode} />
          </AppContainer>

          {loader && loader.visible && <Spin tip={loader.label} fullscreen />}
          <Toast />

          <Overlay
            visible={showUserGuide}
            onClose={() => dispatch(userReducer.setShowUserGuide(false))}
          >
            <OverlayTitle level={5}>{t('Welcome to our system!')}</OverlayTitle>
            <OverlayText>
              {t(
                'Hello! We are pleased to welcome you to the Spirit Capital Investments client portal.'
              )}
              <br />
              {t('What can you do here?')}
              <ol>
                <li>
                  {t(
                    'Get Verified: First, the system will ask you to verify your identity. This is a mandatory step for your security.'
                  )}
                </li>
                <li>
                  {t(
                    'Fill out Questionnaires: After successful verification, you will be able to fill out all the necessary client questionnaires.'
                  )}
                </li>
                <li>
                  {t(
                    'Apply for Products: Once your questionnaires are reviewed and accepted, you can browse our investment products and submit applications for them.'
                  )}
                </li>
              </ol>
              {t(
                'If you are working with one of our agents or assistants, they will be able to see your profile and help you with the questionnaires.'
              )}
              <br />
              {t('If you have any questions at any stage, please contact us:')}
              <br />
              {t('Email')}:
              <ExternalLink
                fontSize={16}
                href={`mailto:${emailStatic && emailStatic?.text ? emailStatic.text : ''}`}
                external
              >
                {emailStatic && emailStatic?.text ? emailStatic.text : ''}
              </ExternalLink>
              <br />
              {t('Phone')}:
              <ExternalLink
                fontSize={16}
                href={`mailto:${phoneStatic && phoneStatic?.text ? phoneStatic.text : ''}`}
                external
              >
                {phoneStatic && phoneStatic?.text ? phoneStatic.text : ''}
              </ExternalLink>
            </OverlayText>
          </Overlay>

          <Overlay visible={showBrowserCompatibility} onClose={handleCloseUserGuide}>
            <span style={{ fontWeight: 700 }}>{t('Attention!')}</span>
            <br />
            {t('The system is optimized for use in the Google Chrome browser.')}
            {t(' ')}
            {t(
              'To ensure all features work correctly, we recommend using Chrome version 135.0.7049.95/96 or later.'
            )}
          </Overlay>
        </>
      )}

      {!routerMode && (
        <>
          <Loading />
        </>
      )}
    </ScrollToTop>
  );
};

export default App;

const AppContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
`;

const OverlayTitle = styled(Typography.Title)`
  &&& {
    margin-bottom: 12px;

    @media (max-width: ${BREAKPOINTS.smd}px) {
      font-size: 16px !important;
    }
  }
`;

const OverlayText = styled(Typography.Text)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    font-size: 13px !important;
  }
`;
